import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaExternalLinkAlt, FaMobileAlt, FaServer } from 'react-icons/fa';
import Header from './Header';
import Footer from './Footer';
import BlueprintSectionTitle from './BlueprintSectionTitle';
import { ink, inkMid, sky, paper, paperMuted, borderInk, borderInkHover, bodyMuted } from '../theme';

const Page = styled.div`
  min-height: 100vh;
  background: ${paperMuted};
  padding-top: 80px;
  font-family: 'Inter', system-ui, sans-serif;
`;

const Section = styled.section`
  padding: 4.5rem 0 5.5rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Intro = styled.p`
  margin: 0 auto 2rem;
  max-width: 760px;
  text-align: center;
  color: ${bodyMuted};
  font-size: 1rem;
  line-height: 1.65;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  color: ${inkMid};
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;

  &:hover {
    color: ${ink};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${borderInkHover};
    box-shadow: 0 10px 28px rgba(3, 26, 56, 0.08);
    transform: translateY(-2px);
  }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px 16px calc(18px + 5px);
  position: relative;
  overflow: hidden;
  background: linear-gradient(168deg, #0e3a63 0%, ${ink} 52%, #021427 100%);
  border-bottom: 1px solid rgba(142, 207, 255, 0.22);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.12;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px);
    background-size: 12px 12px;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, ${sky} 0%, rgba(142, 207, 255, 0.35) 100%);
    box-shadow: 3px 0 12px rgba(142, 207, 255, 0.18);
    pointer-events: none;
  }
`;

const IconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${sky};
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CardTitle = styled.h3`
  position: relative;
  z-index: 1;
  margin: 0;
  color: #f8fbff;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Body = styled.div`
  padding: 1rem 1.15rem 1.2rem;
`;

const Description = styled.p`
  margin: 0 0 1rem;
  color: ${bodyMuted};
  line-height: 1.6;
  font-size: 0.95rem;
`;

const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  text-decoration: none;
  background: ${ink};
  color: #f8fbff;
  border: 1px solid rgba(142, 207, 255, 0.32);
  border-left: 3px solid ${sky};
  padding: 0.58rem 0.92rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${inkMid};
    border-color: rgba(142, 207, 255, 0.48);
    transform: translateY(-1px);
  }
`;

const ResourcesPage = () => {
  const resources = [
    {
      title: 'Rocket.Chat для Android',
      description: 'Клиент Rocket.Chat для мобильных Android-устройств.',
      href: 'https://disk.yandex.ru/d/ppTdKr2A_3QVYg',
      icon: <FaMobileAlt />,
    },
    {
      title: 'Образ ВМ с E-node cloud',
      description: 'Готовый образ виртуальной машины с установленным E-node cloud.',
      href: 'https://disk.yandex.ru/d/BjhZm5b9DEoatg',
      icon: <FaServer />,
    },
  ];

  return (
    <Page>
      <Header />
      <Section>
        <Container>
          <BackLink to="/">
            <FaArrowLeft /> На главную
          </BackLink>
          <BlueprintSectionTitle>Ресурсы</BlueprintSectionTitle>
          <Intro>
            Закрытый раздел с дополнительными материалами, дистрибутивами и образами для работы с E-node cloud.
          </Intro>

          <Grid>
            {resources.map(item => (
              <Card key={item.href}>
                <Head>
                  <IconWrap>{item.icon}</IconWrap>
                  <CardTitle>{item.title}</CardTitle>
                </Head>
                <Body>
                  <Description>{item.description}</Description>
                  <LinkButton href={item.href} target="_blank" rel="noreferrer noopener">
                    Открыть ссылку <FaExternalLinkAlt />
                  </LinkButton>
                </Body>
              </Card>
            ))}
          </Grid>
        </Container>
      </Section>
      <Footer />
    </Page>
  );
};

export default ResourcesPage;
